import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { GripVertical, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function GroupSortModal({ onClose }) {
  const [groups, setGroups] = useState([]);
  const [orderedGroups, setOrderedGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsRes, orderRes] = await Promise.all([
          api.get('/product-groups'),
          api.get('/user-group-order')
        ]);
        const groupMap = {};
        groupsRes.data.forEach(g => { groupMap[g.id] = g; });
        const orderedIds = orderRes.data.group_ids || [];
        const ordered = [];
        orderedIds.forEach(id => {
          if (groupMap[id]) {
            ordered.push(groupMap[id]);
            delete groupMap[id];
          }
        });
        Object.values(groupMap).forEach(g => ordered.push(g));
        setGroups(groupsRes.data);
        setOrderedGroups(ordered);
      } catch { toast.error('Kunne ikke hente grupper'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOverItem.current = idx; };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const items = [...orderedGroups];
    const draggedItem = items[dragItem.current];
    items.splice(dragItem.current, 1);
    items.splice(dragOverItem.current, 0, draggedItem);
    dragItem.current = null;
    dragOverItem.current = null;
    setOrderedGroups(items);
  };

  const moveItem = useCallback((fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= orderedGroups.length) return;
    const items = [...orderedGroups];
    const item = items[fromIdx];
    items.splice(fromIdx, 1);
    items.splice(toIdx, 0, item);
    setOrderedGroups(items);
  }, [orderedGroups]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/user-group-order', { group_ids: orderedGroups.map(g => g.id) });
      toast.success('Rækkefølge gemt');
      onClose();
    } catch { toast.error('Kunne ikke gemme rækkefølge'); }
    finally { setSaving(false); }
  };

  // Touch drag support
  const touchStart = useRef(null);
  const touchIdx = useRef(null);

  const handleTouchStart = (idx, e) => {
    touchIdx.current = idx;
    touchStart.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (touchIdx.current === null || !touchStart.current) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStart.current;
    const itemHeight = 52;
    const moveBy = Math.round(diff / itemHeight);
    if (moveBy !== 0) {
      const newIdx = Math.max(0, Math.min(orderedGroups.length - 1, touchIdx.current + moveBy));
      if (newIdx !== touchIdx.current) {
        const items = [...orderedGroups];
        const item = items[touchIdx.current];
        items.splice(touchIdx.current, 1);
        items.splice(newIdx, 0, item);
        setOrderedGroups(items);
        touchIdx.current = newIdx;
        touchStart.current = currentY;
      }
    }
  };

  const handleTouchEnd = () => {
    touchIdx.current = null;
    touchStart.current = null;
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#EAE6DF] max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#2F3E3B]">Sortér varegrupper</DialogTitle>
          <p className="text-sm text-[#5C706A] font-sans">Træk grupperne i den rækkefølge du ønsker</p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-2 px-2 py-1" onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
          {loading ? (
            <div className="text-center py-8 text-[#5C706A] font-sans">Indlæser...</div>
          ) : (
            <div className="space-y-1">
              {orderedGroups.map((group, idx) => (
                <div
                  key={group.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragEnter={() => handleDragEnter(idx)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  onTouchStart={(e) => handleTouchStart(idx, e)}
                  className="flex items-center gap-2 px-3 py-2.5 bg-white border border-[#EAE6DF] rounded-lg cursor-grab active:cursor-grabbing hover:border-[#7F9C82]/40 hover:bg-[#7F9C82]/5 transition-all select-none group"
                  data-testid={`sort-group-${group.id}`}
                >
                  <GripVertical className="w-4 h-4 text-[#EAE6DF] group-hover:text-[#7F9C82] shrink-0 transition-colors" />
                  <span className="text-xs font-mono text-[#5C706A]/60 w-5 text-right shrink-0">{idx + 1}</span>
                  <span className="font-sans text-sm text-[#2F3E3B] flex-1">{group.name}</span>
                  <div className="flex gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => moveItem(idx, idx - 1)} disabled={idx === 0}
                      className="p-1 rounded hover:bg-[#EAE6DF] text-[#5C706A] disabled:opacity-20"
                      data-testid={`sort-up-${group.id}`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 15l-6-6-6 6"/></svg>
                    </button>
                    <button onClick={() => moveItem(idx, idx + 1)} disabled={idx === orderedGroups.length - 1}
                      className="p-1 rounded hover:bg-[#EAE6DF] text-[#5C706A] disabled:opacity-20"
                      data-testid={`sort-down-${group.id}`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="pt-2 border-t border-[#EAE6DF]">
          <Button variant="outline" onClick={onClose} className="font-sans">Annuller</Button>
          <Button onClick={handleSave} disabled={saving}
            className="bg-[#7F9C82] hover:bg-[#658368] text-white font-sans" data-testid="save-group-order-button">
            <Check className="w-4 h-4 mr-1" />{saving ? 'Gemmer...' : 'Gem rækkefølge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
